// src/pages/SingleRecipePage.js
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FaClock,
  FaUtensils,
  FaConciergeBell,
  FaList,
  FaPrint,
  FaCheckCircle,
  FaArrowDown,
  FaYoutube,
  FaInstagram,
  FaTiktok,
} from "react-icons/fa";
import "./SingleRecipePage.css";

function SingleRecipePage() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);

  // --- PIN TO PINTEREST FUNCTIONALITY ---
  const handlePinClick = () => {
    // current page URL, image URL and description
    const url = window.location.href;
    const imageUrl = recipe.images?.[0]?.image_url;
    const description = `Recipe for ${recipe.title}: ${recipe.short_description}`;

    // constructing a Pinterest share URL
    const pinterestUrl = `https://www.pinterest.com/pin/create/button/?` +
                          `url=${encodeURIComponent(url)}` +
                          `&media=${encodeURIComponent(imageUrl)}` +
                          `&description=${encodeURIComponent(description)}`;

    window.open(pinterestUrl, '_blank', 'width=750, height=550');
  };


  // --- UTILITY FUNCTION TO CAPITALIZE FIRST LETTER ---
  const capitalizeWords = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

// --- FETCH RECIPE DATA ---
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/recipes/${id}/`)
      .then((res) => setRecipe(res.data));
  }, [id]);

  if (!recipe) return <p>Loading...</p>;

  // --- IMAGES ---
  const images = recipe.images || [];
  const firstImage = images[0]?.image_url;

  // --- NOTES PARAGRAPHS ---
  let recipeNotesArray = [];
  if (recipe.notes) {
    try{
      recipeNotesArray = JSON.parse(recipe.notes);
    } catch (error) {
      console.error("Error parsing notes JSON:", error);
    }
  }

  // --- DESCRIPTION PARAGRAPHS ---
  const longDescriptionParagraphs = recipe.long_description
    ? recipe.long_description.split("\\n\\n").filter((p) => p.trim() !== "")
    : [];

  const updatedDate = recipe.updated_at
    ? new Date(recipe.updated_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="single-recipe-container">

      {/* ---------- TITLE & META ---------- */}
      <h1 className="recipe-title">{recipe.title}</h1>
      <p className="updated-text">Last updated on {updatedDate}</p>

      <div className="top-buttons">
        <a href="#recipe-card" className="jump-btn">
          Jump to Recipe <FaArrowDown />
        </a>

        {/* Pinterest share (you can wire this later) */}
        <button className="pinterest-btn" onClick={handlePinClick}>
          📌 Pin to Pinterest
        </button>
      </div>

      {/* ---------- LONG DESCRIPTION WITH IMAGES ---------- */}
      <div className="long-description">
        {longDescriptionParagraphs.map((paragraph, index) => (
          <div key={index}>
            <p>{paragraph}</p>

            {/* Show first image after first paragraph */}
            {index === 0 && firstImage && (
              <img
                src={firstImage}
                alt={recipe.title}
                className="long-desc-main-img"
              />
            )}
          </div>
        ))}

        {/* Remaining images (if any) */}
        {images.slice(1).map((img, index) => (
          <img
            key={index}
            src={img.image_url}
            alt={`Step ${index + 2}`}
            className="long-desc-extra-img"
          />
        ))}
      </div>

      {/* ---------- SOCIAL LINKS ---------- */}
      <div className="social-links">
        {recipe.youtube_video_url && (
          <a href={recipe.youtube_video_url} target="_blank" rel="noreferrer">
            <FaYoutube /> Watch on YouTube
          </a>
        )}

        {recipe.tiktok_video_url && (
          <a href={recipe.tiktok_video_url} target="_blank" rel="noreferrer">
            <FaTiktok /> View on TikTok
          </a>
        )}

        {recipe.instagram_video_url && (
          <a href={recipe.instagram_video_url} target="_blank" rel="noreferrer">
            <FaInstagram /> View on Instagram
          </a>
        )}
      </div>

      {/* ---------- RECIPE CARD ---------- */}
      <div className="recipe-card-container" id="recipe-card">

        <div className="recipe-card-header">
          <div className="recipe-card-text">
            <h2>{recipe.title}</h2>
            <p className="short-desc">{recipe.short_description}</p>

            <button className="print-btn" onClick={() => window.print()}>
              <FaPrint /> Print Recipe
            </button>
          </div>

          {firstImage && (
            <img
              src={firstImage}
              alt={recipe.title}
              className="recipe-card-img"
            />
          )}
        </div>

        {/* META INFO */}
        <div className="recipe-meta">
          <div><FaClock /> Prep: {recipe.prep_time} mins</div>
          <div><FaClock /> Cook: {recipe.cook_time} mins</div>
          <div><FaClock /> Marinate: {recipe.mariantion_time} mins</div>
          <div><FaClock /> Total: {recipe.total_time} mins</div>
          <div><FaList /> Servings: {recipe.servings}</div>
          <div><FaUtensils /> Course: {capitalizeWords(recipe.category)}</div>
          <div><FaConciergeBell /> Cuisine: {capitalizeWords(recipe.cuisine)}</div>
        </div>

        {/* NAV BUTTONS */}
        <div className="section-nav">
          <a href="#equipment">Equipment</a>
          <a href="#ingredients">Ingredients</a>
          <a href="#instructions">Instructions</a>
        </div>

        {/* ---------- EQUIPMENT ---------- */}
        <div className="recipe-section" id="equipment">
          <h3>Equipment</h3>
          <ul className="checklist">
            {recipe.equipment.map((item, i) => (
              <li key={i}>
                <FaCheckCircle className="check-icon" /> {item}
              </li>
            ))}
          </ul>
        </div>

        {/* ---------- INGREDIENTS WITH SECTIONS ---------- */}
        <div className="recipe-section" id="ingredients">
          <h3>Ingredients</h3>

          {Object.keys(recipe.ingredients).map((sectionName, i) => (
            <div key={i} className="ingredient-section">
              <h4>{sectionName.toUpperCase()}</h4>
              <ul>
                {recipe.ingredients[sectionName].map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ---------- INSTRUCTIONS WITH SECTIONS ---------- */}
        <div className="recipe-section" id="instructions">
          <h3>Instructions</h3>

          {Object.keys(recipe.steps).map((sectionName, i) => (
            <div key={i} className="instruction-section">
              <h4>{sectionName.toUpperCase()}</h4>
              <ol>
                {recipe.steps[sectionName].map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>

        {/* ---------- NOTES ---------- */}
        {recipeNotesArray.length > 0 && (
          <div className="recipe-section notes-section" id="notes">
            <h3>Chef's Notes & Tips</h3>
            <ol>
              {recipeNotesArray.map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ol>
          </div>
        )}

      </div>
    </div>
  );
}

export default SingleRecipePage;
